/**
 * THE match screen.
 *
 * Full-bleed globe with the HUD floating over it: ladders at the top where they
 * are always in peripheral vision, prompt and actions at the bottom where a
 * thumb reaches them. On a wide viewport the HUD becomes a side column and the
 * globe keeps the rest — same components, same state, one layout switch in CSS.
 *
 * Everything here is a projection of `MatchState` from @geogame/rules. This file
 * decides nothing about the game: not whether a push is legal, not when a rung
 * expires, not what a pot is worth. It reads and it renders.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { TIER_CONFIG, cityDistanceKm, rungOptions } from '@geogame/shared';
import type { LngLat, ProbeType } from '@geogame/shared';
import { encodeOrdering, potAfter } from '@geogame/rules/scoring';
import type { GlobePick } from '@geogame/globe';

import { ConnectionBanner } from '../components/ConnectionBanner';
import { MapChoiceProbe } from '../components/MapChoiceProbe';
import { US_HOME_BOUNDS, nearestMarker, useSubdivisionRegions } from '../lib/regions';

import { DecisionBar } from '../components/DecisionBar';
import { DualLadder } from '../components/DualLadder';
import { FlameRow } from '../components/Flame';
import { GlobeStage } from '../components/GlobeStage';
import { NumericPad } from '../components/NumericPad';
import { OptionGrid } from '../components/OptionGrid';
import { OrderingProbe } from '../components/OrderingProbe';
import { PinpointAttempts } from '../components/PinpointAttempts';
import { PinKey } from '../components/PinKey';
import type { PinKeyRow } from '../components/PinKey';
import { PromptCard } from '../components/PromptCard';
import { RoundReveal } from '../components/RoundReveal';
import { RungReveal } from '../components/RungReveal';
import { TrueFalseProbe } from '../components/TrueFalseProbe';
import { coachLine, isOneShot } from '../lib/coach';
import type { CoachBeat } from '../lib/coach';
import { points, rungLabel, rungTitle, tierLabel } from '../lib/format';
import { isSealedInline } from '../lib/crypto';
import { useGame } from '../store/gameStore';
import { coachBeatSeen, markCoachBeatSeen } from '../store/storage';

/**
 * Pot projections for the button labels. Derived from `potAfter` rather than
 * from a second copy of the multiplier table — a display that disagreed with
 * the scoring would be worse than no display at all.
 */
/**
 * How long a probe may be pending before we admit it is not coming.
 *
 * Comfortably longer than the driver's own 2.5s artwork budget, so this only
 * fires when something below that has genuinely failed rather than merely
 * taken its time.
 */
const PROBE_STALL_MS = 4_000;

const MULT_UNIT = 1_000_000;
function multiplierOf(rung: number, golden: boolean, ladderDepth: number): number {
  return potAfter(rung, MULT_UNIT, { golden, ladderDepth }) / MULT_UNIT;
}
/**
 * What the pot becomes on a push.
 *
 * CHANGE 6 rides in through `golden`: on the golden round the TERMINAL rung's
 * multiplier is tripled, so the push that enters it is worth x9 rather than x3
 * and the button has to say so. Same `potAfter` the server scores with, with
 * the same options, so the number on the button is the number in the bank.
 */
function projectPot(
  pot: number,
  fromRung: number,
  toRung: number,
  golden: boolean,
  ladderDepth: number,
): number {
  if (pot <= 0 || fromRung < 1 || toRung < 1) return 0;
  return Math.round(
    pot * (multiplierOf(toRung, golden, ladderDepth) / multiplierOf(fromRung, golden, ladderDepth)),
  );
}

export function MatchScreen(): React.JSX.Element {
  const match = useGame((s) => s.match);
  const round = useGame((s) => s.round);
  const matchInfo = useGame((s) => s.matchInfo);
  const tier = useGame((s) => s.tier);
  const gameMode = useGame((s) => s.gameMode);
  const handle = useGame((s) => s.handle);
  const status = useGame((s) => s.status);
  const rttMs = useGame((s) => s.rttMs);
  const oppPhase = useGame((s) => s.oppPhase);
  const eclipsed = useGame((s) => s.eclipsed);
  const eclipseRound = useGame((s) => s.eclipseRound);
  const oppSeq = useGame((s) => s.oppSeq);
  const history = useGame((s) => s.history);
  const goldenRoundIdx = useGame((s) => s.goldenRoundIdx);
  const coached = useGame((s) => s.coached);
  const serverNow = useGame((s) => s.serverNow);
  const submitPinpoint = useGame((s) => s.submitPinpoint);
  const submitAnswer = useGame((s) => s.submitAnswer);
  const submitNumeric = useGame((s) => s.submitNumeric);
  const bank = useGame((s) => s.bank);
  const push = useGame((s) => s.push);
  const leaveMatch = useGame((s) => s.leaveMatch);

  const cfg = TIER_CONFIG[tier];
  const depth = round?.ladderDepth ?? cfg.ladderDepth;
  const phase = match?.phase ?? 'idle';
  const rung = match?.round?.rung ?? 1;
  const pot = match?.round?.pot ?? 0;
  const revealed = round?.revealed === true;
  // CHANGE 6. Golden is known from the arm, one lead-time before the prompt, so
  // the fire is already burning when the question lands.
  const golden = round?.golden === true || match?.round?.golden === true;
  // A deeper rung is pre-delivered SEALED and its key is released only when the
  // player commits to it, so for about one RTT after pushing the rung exists but
  // its prompt is still a `sealed:1:` blob. Treat that as not-yet-arrived rather
  // than rendering base64 at the player.
  const rungEntry = round?.rungs[rung];
  const rungPayload = rungEntry && isSealedInline(rungEntry.prompt) ? undefined : rungEntry;
  const probeType: ProbeType | 'pinpoint' = rungPayload?.probeType ?? 'pinpoint';
  // Object URLs for a picture rung, or undefined until its sealed artwork
  // bundle has been opened with the same key that opened its prompt.
  const rungImageUrls = rungPayload ? round?.assetUrls[rungPayload.rung] : undefined;
  // The subdivision mode. `regions` is what puts the globe into it, so it is
  // read here — above every early return — rather than next to the JSX.
  const stateMode = gameMode === 'state';
  const regions = useSubdivisionRegions(stateMode);
  /*
   * The live map choice's dots, or null.
   *
   * Non-null means the globe is currently the answer control: it drives the
   * dots, the tap routing in `onPick`, and whether the globe takes taps at all.
   * One source for all three, so they cannot disagree about which question is
   * open.
   */
  const choiceMarkers =
    phase === 'probe' && rungPayload?.kind === 'choice' && rungPayload.markers
      ? rungPayload.markers
      : null;
  // A probe that never arrives used to render "Loading the probe…" forever, with
  // no timeout and no way out — the player's only escape was to leave the match.
  // Past this budget we say so and hand them their pot back.
  const probePending = phase === 'probe' && !rungPayload;
  const [probeStalled, setProbeStalled] = useState(false);
  useEffect(() => {
    if (!probePending) {
      setProbeStalled(false);
      return undefined;
    }
    const t = window.setTimeout(() => setProbeStalled(true), PROBE_STALL_MS);
    return () => window.clearTimeout(t);
  }, [probePending, rung, round?.roundIdx]);

  const lastRound = history.length > 0 ? history[history.length - 1] : undefined;
  // The LIVE frame wins over the one `matched` opened with. In a duel they are
  // the same name forever; in a group match the subject is re-chosen each round
  // as the standings move, and `matched` only ever knew the first one.
  const opponentHandle =
    match?.opponent?.handle || matchInfo?.opponent.handle || 'opponent';

  const deadline = match?.round?.deadlineServerTs ?? null;
  /*
   * The ring has to be as long as the window it is counting down.
   *
   * Rung 1 carries a reading allowance when its prompt is a sentence rather
   * than a name, and the machine has already spent that on the deadline above.
   * A ring sized without it would empty five seconds early on exactly the
   * rounds the allowance exists for, which reads as the clock cheating.
   */
  const windowMs =
    phase === 'decision'
      ? (round?.decisionSeconds ?? cfg.decisionSeconds) * 1000
      : (round?.secondsPerRung ?? cfg.secondsPerRung) * 1000 +
        (phase === 'pinpoint' ? (round?.readSeconds ?? 0) * 1000 : 0);

  const yourPin: LngLat | null = match?.round?.pinLngLat ?? null;
  const opponentPin: LngLat | null = match?.opponent?.lngLat ?? null;

  /**
   * The pin key. Owned here rather than by the globe so that it renders inside
   * the HUD's own flow — parked over the globe it sat on top of the BANK button
   * on a phone. `targetPinShown` comes back from the globe because only the
   * globe knows when the gold pin has finished landing, and a key must never
   * name a marker that is not on screen yet.
   */
  const [targetPinShown, setTargetPinShown] = useState(false);

  /*
   * ONE TAP HANDLER, TWO QUESTIONS.
   *
   * The globe is the answer surface for rung 1 in every mode and for rung 2 in
   * the subdivision mode, so a tap has to be routed by what is being asked
   * rather than by where it landed. `choiceMarkers` is the discriminator, and
   * it is non-null only while a live map-choice rung is on screen — the same
   * array the dots are drawn from, so the control and the display cannot
   * disagree about which question is open.
   */
  const onPick = useCallback(
    (pick: GlobePick) => {
      if (choiceMarkers) {
        const idx = nearestMarker(choiceMarkers, pick.lngLat);
        if (idx !== null) submitAnswer(idx);
        return;
      }
      // The store's gate discards anything before startAt; nothing is queued.
      submitPinpoint(pick.lngLat, pick.cca3);
    },
    [choiceMarkers, submitAnswer, submitPinpoint],
  );

  const onOrder = useCallback(
    (order: readonly number[]) => {
      // The ordering answer rides `optionIdx` as a permutation rank.
      submitAnswer(encodeOrdering([...order]));
    },
    [submitAnswer],
  );

  const ladder = useMemo(
    () => ({
      you: {
        handle,
        rung: match?.round?.rung ?? 0,
        total: match?.bankedTotal ?? 0,
        pot,
        phase,
        banked: match?.round?.banked === true,
        busted:
          match?.round?.outcome === 'busted' ||
          match?.round?.outcome === 'missed' ||
          match?.round?.outcome === 'timeout',
      },
      opponent: {
        handle: opponentHandle,
        rung: match?.opponent?.rung ?? 0,
        total: match?.opponent?.total ?? 0,
        phase: oppPhase,
        banked: match?.opponent?.banked === true,
        seq: oppSeq,
        fieldHighRung: match?.opponent?.fieldHighRung ?? 0,
      },
    }),
    [handle, match, opponentHandle, oppPhase, oppSeq, phase, pot],
  );

  /*
   * EVERY HOOK ABOVE THE EARLY RETURN, and the coach block comes with them
   * because the effect at the end of it is one.
   *
   * It used to sit below `if (!match)`, which meant this component rendered
   * a different NUMBER of hooks depending on whether a match was in
   * progress. React throws the moment that flips -- leaving a match while
   * still on this screen is enough -- and neither the type checker nor a
   * test that always had a match could see it. `rules-of-hooks` found it in
   * the first second the linter existed.
   *
   * Everything here reads through `?.` or a null guard already, so it is
   * safe to compute before the match is known: with no match there is no
   * round, no beat, and the effect does nothing.
   */
  const cityMode = gameMode === 'city';

  /**
   * Which beat, if any, this moment is.
   *
   * BUST OUTRANKS ROUND-END, because they collide: a round that ends by losing
   * the pot is both, and "that's the loop, four more rounds" is the wrong
   * thing to say to somebody who has just watched a number disappear.
   *
   * GOLDEN-ARMED is checked against the arm phase rather than the prompt, so
   * the warning lands one lead-time before the question rather than as a
   * caption on a clock already running.
   */
  const coachBeat: CoachBeat | null =
    phase === 'armed' && golden
      ? 'golden-armed'
      : phase === 'round-end' && match?.round?.outcome === 'busted'
        ? 'bust'
        : phase === 'pinpoint' && revealed
          ? 'pinpoint'
          : phase === 'decision'
            ? 'decision'
            : phase === 'probe' && rungPayload
              ? 'probe'
              : phase === 'round-end'
                ? 'round-end'
                : null;

  /*
   * WHO IS ELIGIBLE FOR THIS BEAT.
   *
   * The loop beats need the player to have asked to be coached. The one-shot
   * beats do not, and that is the point of them: a golden round and a lost pot
   * ambush somebody who skipped the tutorial exactly as hard as somebody who
   * took it, and they are the two moments most likely to end a first session.
   * They fire once ever, and `coachBeatSeen` is read live so the card
   * disappears the moment it is marked.
   */
  const beatEligible =
    coachBeat !== null && (isOneShot(coachBeat) ? !coachBeatSeen(coachBeat) : coached);

  const coach =
    beatEligible && coachBeat !== null && round !== null
      ? coachLine(coachBeat, {
          roundIdx: round.roundIdx,
          attemptsLeft: cityMode
            ? null
            : round.pinpointAttempts.maxAttempts - (round.attempts?.attempt ?? 0),
          potNow: pot,
          potIfPushed: projectPot(pot, rung, rung + 1, golden, depth),
          cityMode,
          stateMode,
          /*
           * WHAT THE BUST COST, taken from the server's round summary rather
           * than from `pot`. The local machine zeroes the pot the instant it
           * busts (invariant 9 — "the current round's pot is lost"), so by the
           * time this renders the live number is 0 and quoting it would tell
           * the player they lost nothing.
           *
           * Matched on roundIdx because `history` still holds the PREVIOUS
           * round's summary during the beat between a local bust and the
           * server's `round.end` landing, and quoting the last round's pot at
           * somebody who just lost this one is worse than quoting no number.
           */
          potLost:
            lastRound !== undefined && lastRound.roundIdx === round.roundIdx
              ? lastRound.you.pot
              : 0,
          bankedTotal: match?.bankedTotal ?? 0,
        })
      : null;

  /*
   * Burn a one-shot once it has actually been on screen.
   *
   * Marked in an effect rather than during render, and keyed on the beat, so a
   * re-render inside the same phase does not retire the line before the player
   * has finished reading it — and React's strict double-render cannot either.
   */
  useEffect(() => {
    if (coach === null || coachBeat === null || !isOneShot(coachBeat)) return;
    markCoachBeatSeen(coachBeat);
  }, [coach, coachBeat]);

  if (!match) {
    return (
      <main className="gg-screen gg-center">
        <p className="opacity-70">No match in progress.</p>
        <button type="button" className="gg-btn gg-btn-primary mt-4" onClick={leaveMatch}>
          Back to home
        </button>
      </main>
    );
  }

  // The globe takes taps for rung 1 in every mode, and for a live map choice in
  // the subdivision mode. `awaiting` is checked so that a second tap while the
  // first is still in flight cannot spend the rung twice.
  const interactive =
    (phase === 'pinpoint' && revealed) ||
    (choiceMarkers !== null && round?.awaitingRung === null);
  const cityPinResolved =
    cityMode && round?.targetLngLat !== null && round?.targetLngLat !== undefined && yourPin !== null;
  const cityDistance =
    cityPinResolved && round?.targetLngLat ? cityDistanceKm(yourPin, round.targetLngLat) : null;
  const awaiting = round?.awaitingRung !== null && round?.awaitingRung !== undefined;

  /**
   * What to call the place on the decision card.
   *
   * `targetLabel` lands with the PINPOINT reveal, which is the event that opens
   * this phase, so it is populated by the time anything here renders. The
   * fallback is country mode only and deliberately so: a country's rung-1
   * prompt IS its name, while a city or a state can be prompted by a clue at
   * the harder tiers, and quoting the clue back would read as though the game
   * still had not told you what you found.
   */
  const decisionTarget =
    round?.targetLabel ?? (gameMode === 'country' ? (round?.prompt ?? null) : null);
  // How many rounds until the fire. Shown on the arm card of the round BEFORE
  // it, so the golden round is anticipated rather than merely encountered.
  const goldenNext =
    goldenRoundIdx !== null && round !== null && goldenRoundIdx === round.roundIdx + 1;

  const pinKeyRows: readonly PinKeyRow[] = [
    targetPinShown && round?.targetLngLat ? { kind: 'target' as const, label: 'Answer' } : null,
    yourPin ? { kind: 'you' as const, label: 'You' } : null,
    yourPin && opponentPin ? { kind: 'opponent' as const, label: opponentHandle } : null,
  ].filter((row): row is PinKeyRow => row !== null);

  // CHANGE 4. A miss has to FEEL like a miss. Remounting the card on this key
  // is what replays the jolt: two misses in a row must shake twice, and
  // re-triggering a CSS animation on a surviving element needs a reflow hack
  // that is worse than remounting one card.
  const missSeq = round?.pickFeedback?.retryable === true ? round.pickFeedback.seq : 0;

  /*
   * The resolved map choice, for the globe to pin. Built from the rung's own
   * dots and the reveal's indices; both halves are post-resolution, because
   * `round.reveal` only exists for a rung nobody can answer any more.
   */
  const revealedRung = round?.reveal ? round.rungs[round.reveal.rung] : undefined;
  const choiceReveal =
    phase === 'round-end' &&
    round?.reveal?.kind === 'choice' &&
    revealedRung?.kind === 'choice' &&
    revealedRung.markers
      ? {
          markers: revealedRung.markers,
          correctIndex: round.reveal.correctIndex,
          pickedIndex: round.reveal.pickedIndex,
        }
      : null;

  return (
    <main className="gg-match" data-golden={golden ? 'true' : 'false'} data-phase={phase}>
      <GlobeStage
        roundIdx={round?.roundIdx ?? null}
        interactive={interactive}
        snapRadiusPx={cfg.snapRadiusPx}
        orientRegion={round?.orientRegion ?? null}
        yourPin={yourPin}
        eclipsed={eclipsed}
        opponentPin={opponentPin}
        pickFeedback={round?.pickFeedback ?? null}
        // INVARIANT 5. The globe only ever travels to the answer once the ROUND
        // is over. A resolved pinpoint mid-round is confirmed in place by
        // `pickFeedback`; it does not move the camera, because on a correct
        // pinpoint that would be noise and on a busted one the round has ended
        // anyway.
        reveal={phase === 'round-end' ? (round?.reveal ?? null) : null}
        revealCca3={phase === 'round-end' || cityPinResolved ? (round?.targetCca3 ?? null) : null}
        revealLngLat={phase === 'round-end' || cityPinResolved ? (round?.targetLngLat ?? null) : null}
        exactTarget={cityMode}
        regions={regions}
        homeBounds={stateMode ? US_HOME_BOUNDS : null}
        choiceMarkers={choiceMarkers}
        choiceReveal={choiceReveal}
        onTargetPinShown={setTargetPinShown}
        onPick={onPick}
      />

      <div className="gg-hud">
        <div className="gg-hud-top">
          <ConnectionBanner status={status} rttMs={rttMs} inMatch />
          <div className="gg-hud-meta">
            <span className="tabular-nums">
              Round {(match.roundIdx >= 0 ? match.roundIdx : 0) + 1} / {match.roundCount}
            </span>
            <span className="uppercase tracking-widest opacity-60">{tierLabel(tier)}</span>
          </div>
          {/*
        THE CAPTION IS NOT DECORATION.
        ===============================================================
        A globe that quietly loses its borders is indistinguishable from a
        rendering fault, and a player who thinks the game broke stops trusting
        it. So the eclipse SAYS what it is, on screen, for as long as it lasts.
        The animation makes it look deliberate; this makes it legible.

        Two states, because they mean different things. Being eclipsed is a
        penalty already served on you; playing the eclipse round is a warning
        that one is being decided.
      */}
      {eclipsed ? (
        <div className="gg-eclipse-banner" role="status">
          <span className="gg-eclipse-mark" aria-hidden="true" />
          <span>
            <strong>Eclipse.</strong> No borders this round, coastlines only.
          </span>
        </div>
      ) : eclipseRound ? (
        <div className="gg-eclipse-banner" data-warning="true" role="status">
          <span className="gg-eclipse-mark" aria-hidden="true" />
          <span>
            <strong>Eclipse round.</strong> Out-climb the leader and the shadow falls on them.
          </span>
        </div>
      ) : null}

      <DualLadder depth={depth} you={ladder.you} opponent={ladder.opponent} golden={golden} />
        </div>

        <div className="gg-hud-bottom">
          <PinKey rows={pinKeyRows} />

          {coach ? (
            <aside className="gg-coach" role="note">
              <p className="gg-coach-kicker">{coach.kicker}</p>
              <p className="gg-coach-body">{coach.body}</p>
            </aside>
          ) : null}

          {phase === 'armed' || !revealed ? (
            <section
              className="gg-prompt gg-prompt-armed"
              data-golden={golden ? 'true' : 'false'}
              aria-live="polite"
            >
              {golden ? (
                <>
                  <FlameRow count={5} size={26} />
                  <p className="gg-prompt-kicker gg-golden-kicker">Golden round</p>
                  <h2 className="gg-prompt-question gg-golden-title">
                    Round {(round?.roundIdx ?? 0) + 1} is worth triple at the top
                  </h2>
                  <p className="mt-1 text-xs opacity-70">
                    The last question is an open answer. No options, no guessing.
                  </p>
                </>
              ) : (
                <>
                  <p className="gg-prompt-kicker">Round {(round?.roundIdx ?? 0) + 1}</p>
                  <h2 className="gg-prompt-question">Get ready…</h2>
                  <p className="mt-1 text-xs opacity-60">
                    {goldenNext
                      ? 'Next round is the golden one.'
                      : 'Both players see the prompt at the same instant.'}
                  </p>
                </>
              )}
            </section>
          ) : null}

          {phase === 'pinpoint' && revealed ? (
            <div key={missSeq} className="gg-miss-shake" data-miss={missSeq > 0 ? 'true' : 'false'}>
              <PromptCard
                rung={1}
                probeType="pinpoint"
                prompt={round?.prompt ?? ''}
                deadlineServerTs={deadline}
                durationMs={windowMs}
                serverNow={serverNow}
                golden={golden}
                gameMode={gameMode}
              >
                <p className="text-xs opacity-70">
                  {awaiting
                    ? 'Pin locked. Grading…'
                    : round?.pickFeedback?.retryable === true
                      ? round.pickFeedback.cca3 === null
                        ? 'That was open ocean. Try again.'
                        : 'Not it. Try again.'
                      : 'Tap the globe to drop your pin.'}
                </p>
                {round && !cityMode ? (
                  <PinpointAttempts
                    policy={round.pinpointAttempts}
                    spent={round.attempts?.attempt ?? 0}
                    startAtServerTs={round.startAtServerTs}
                    serverNow={serverNow}
                    pending={awaiting}
                  />
                ) : null}
              </PromptCard>
            </div>
          ) : null}

          {phase === 'probe' && rungPayload ? (
            <PromptCard
              rung={rung}
              probeType={probeType}
              prompt={rungPayload.prompt}
              deadlineServerTs={deadline}
              durationMs={windowMs}
              serverNow={serverNow}
              golden={golden && rungPayload.kind === 'numeric'}
              gameMode={gameMode}
            >
              {rungPayload.kind === 'numeric' ? (
                <NumericPad
                  spec={rungPayload.numeric}
                  disabled={awaiting}
                  resetKey={`${round?.roundIdx ?? 0}:${rungPayload.rung}`}
                  onSubmit={submitNumeric}
                />
              ) : rungPayload.kind === 'boolean' ? (
                <TrueFalseProbe
                  options={rungPayload.options}
                  disabled={awaiting}
                  chosenIdx={null}
                  onChoose={submitAnswer}
                />
              ) : rungPayload.kind === 'ordering' ? (
                <OrderingProbe
                  options={rungPayload.options}
                  disabled={awaiting}
                  onSubmit={onOrder}
                />
              ) : rungPayload.kind === 'choice' && rungPayload.markers && rungPayload.names ? (
                <MapChoiceProbe
                  names={rungPayload.names}
                  markers={rungPayload.markers}
                  awaiting={awaiting}
                />
              ) : rungOptions(rungPayload) ? (
                <OptionGrid
                  options={rungOptions(rungPayload) ?? []}
                  {...(rungImageUrls ? { imageUrls: rungImageUrls } : {})}
                  disabled={awaiting}
                  chosenIdx={null}
                  onChoose={submitAnswer}
                />
              ) : (
                <p className="text-xs opacity-70">Waiting for the probe…</p>
              )}
            </PromptCard>
          ) : null}

          {probePending ? (
            <section className="gg-prompt">
              <p className="gg-prompt-kicker">{rungTitle(rung)}</p>
              {probeStalled ? (
                <>
                  <h2 className="gg-prompt-question">This probe didn&apos;t arrive.</h2>
                  <p className="gg-prompt-sub">
                    Not your fault, and your {points(pot)} is safe. Bank it and play on.
                  </p>
                  <button type="button" className="gg-btn gg-btn-bank" onClick={bank}>
                    Bank {points(pot)}
                  </button>
                </>
              ) : (
                <h2 className="gg-prompt-question">Loading the probe…</h2>
              )}
            </section>
          ) : null}

          {phase === 'decision' ? (
            <div className="flex flex-col gap-2">
              {cityMode && cityDistance !== null ? (
                <div className="gg-city-battle-hit" role="status">
                  <span className="gg-city-battle-name">{round?.targetLabel ?? 'City located'}</span>
                  <span className="gg-city-battle-distance tabular-nums">
                    {cityDistance < 100
                      ? `${cityDistance.toFixed(1)} km away`
                      : `${Math.round(cityDistance).toLocaleString()} km away`}
                  </span>
                </div>
              ) : null}
              <div className="gg-pot-line" data-golden={golden ? 'true' : 'false'}>
                <span className="gg-pot-value tabular-nums">{points(pot)}</span>
                <span className="gg-pot-label">in the pot · {rungLabel(rung)} cleared</span>
              </div>
              <DecisionBar
                potNow={pot}
                potIfPushed={projectPot(pot, rung, rung + 1, golden, depth)}
                nextRung={rung + 1}
                targetLabel={decisionTarget}
                canPush={rung < depth}
                // The push that ENTERS the golden terminal rung is the x9 one.
                goldenPush={golden && rung + 1 === depth}
                deadlineServerTs={deadline}
                decisionMs={windowMs}
                serverNow={serverNow}
                onBank={bank}
                onPush={push}
              />
            </div>
          ) : null}

          {/*
            CHANGE 3. The reveal lands the instant the rung resolves, which is
            BEFORE `round.end` arrives, so this card is on screen while the
            globe is still flying. `round.reveal` only ever exists for a rung
            nobody can answer any more — see the header of RungReveal.
          */}
          {phase === 'round-end' && round?.reveal ? (
            <RungReveal
              reveal={round.reveal}
              rung={round.rungs[round.reveal.rung]}
              correct={round.lastResult?.correct === true}
              golden={golden}
              gameMode={gameMode}
              {...(round.assetUrls[round.reveal.rung]
                ? { imageUrls: round.assetUrls[round.reveal.rung] as readonly string[] }
                : {})}
            />
          ) : null}

          {phase === 'round-end' && lastRound ? (
            <RoundReveal result={lastRound} opponentHandle={opponentHandle} ladderDepth={depth} />
          ) : null}

          {phase === 'match-end' ? (
            <section className="gg-prompt">
              <h2 className="gg-prompt-question">Match complete</h2>
              <p className="mt-1 text-xs opacity-70">Totting up the damage…</p>
            </section>
          ) : null}
        </div>
      </div>

      {/*
        The golden round's ambient light. A fixed overlay behind the HUD and in
        front of the globe, so the whole screen — not just one card — reads as
        different from across the room. Pointer-events none: it is weather, not
        a control.
      */}
      {golden ? <div className="gg-golden-wash" aria-hidden="true" /> : null}
    </main>
  );
}
